import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { API_URL } from "../../config";
import "./css/Customers.css";

const SALES_CUSTOMER_APIS = [
    `${API_URL}/get-sales-customers`,
    `${API_URL}/api/get-sales-customers`,
];
const QUOTATION_APIS = [
    `${API_URL}/api/get-quotations`,
    `${API_URL}/get-quotations`,
];
const RECENT_SALES_VISIT_APIS = [
    `${API_URL}/api/get-sales-customer-visit-reports`,
    `${API_URL}/api/get-recent-sales-visits`,
    `${API_URL}/get-recent-sales-visits`,
];

function Customers({ authInfo }) {
    const [customers, setCustomers] = useState([]);
    const [quotations, setQuotations] = useState([]);
    const [recentVisits, setRecentVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchText, setSearchText] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        const headers = authInfo?.token
            ? {
                  "auth-token": authInfo.token,
                  Authorization: `Bearer ${authInfo.token}`,
              }
            : {};

        const run = async () => {
            setLoading(true);
            setError("");
            try {
                const [customerRes, quotationRes, visitsRes] = await Promise.all([
                    fetchSalesCustomersData(headers),
                    fetchQuotationsData(headers),
                    fetchRecentSalesVisitsData(headers, 300).catch(() => ({ data: [] })),
                ]);
                setCustomers(extractArrayRows(customerRes.data));
                setQuotations(extractArrayRows(quotationRes.data));
                setRecentVisits(extractRecentVisits(visitsRes.data));
            } catch (err) {
                setCustomers([]);
                setQuotations([]);
                setRecentVisits([]);
                setError(err?.message || "Failed to load customer data.");
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [authInfo?.token]);

    const rows = useMemo(() => {
        const latestQuotationByKey = {};
        quotations.forEach((q) => {
            const keys = buildCustomerKeys(q);
            keys.forEach((key) => {
                const existing = latestQuotationByKey[key];
                const qTime = resolveTime(q);
                const eTime = existing ? resolveTime(existing) : 0;
                if (!existing || qTime > eTime) {
                    latestQuotationByKey[key] = q;
                }
            });
        });

        const latestVisitByKey = {};
        recentVisits.forEach((v) => {
            const keys = buildCustomerKeys(v);
            keys.forEach((key) => {
                const existing = latestVisitByKey[key];
                const vTime = resolveTime(v);
                const eTime = existing ? resolveTime(existing) : 0;
                if (!existing || vTime > eTime) {
                    latestVisitByKey[key] = v;
                }
            });
        });

        return customers
            .map((c) => {
                const keys = buildCustomerKeys(c);
                const quotation = keys.map((k) => latestQuotationByKey[k]).find(Boolean) || null;
                const visit = keys.map((k) => latestVisitByKey[k]).find(Boolean) || null;
                const salespersonArea =
                    extractSalespersonArea(visit) ||
                    extractSalespersonArea(quotation) ||
                    extractSalespersonArea(c);

                const priority = formatPriority(
                    c.customer_priority || c.priority || visit?.customer_priority || "C"
                );
                const quotationStatus = formatQuotationStatus(
                    quotation?.status ||
                        quotation?.sale_status ||
                        quotation?.quotation_status ||
                        (quotation ? "sent" : "pending")
                );

                return {
                    id: c.id || c.customer_id || c.cus_id || c.customer_code || c.customer_name,
                    rawVisit: visit,
                    visitDateRaw: visit?.visit_date || visit?.next_followup_date || null,
                    customerName:
                        c.customer_name || c.cus_name || c.name || visit?.customer_name || "-",
                    contact:
                        c.customer_email ||
                        c.email ||
                        c.cus_email ||
                        c.customer_mobile_no ||
                        c.cus_mobile ||
                        "-",
                    priority,
                    quotationStatus,
                    lastContact: formatDateOnly(
                        visit?.visit_date || quotation?.sale_created_isodt || quotation?.sale_date
                    ),
                    salesperson:
                        visit?.sales_person_name ||
                        quotation?.sales_person_name ||
                        quotation?.employee_name ||
                        c.sales_person_name ||
                        "-",
                    location:
                        visit?.location ||
                        visit?.visit_location ||
                        visit?.customer_area ||
                        c.customer_area ||
                        c.customer_address ||
                        "-",
                    notes:
                        visit?.visit_notes ||
                        visit?.previous_feedback ||
                        visit?.previous_plan_feedback ||
                        visit?.notes ||
                        visit?.followup_note ||
                        visit?.note ||
                        "No notes added.",
                    salespersonArea,
                };
            })
            .filter((item) => {
                const text = searchText.trim().toLowerCase();
                return (
                    !text ||
                    item.customerName.toLowerCase().includes(text) ||
                    item.contact.toLowerCase().includes(text)
                );
            })
            .sort((a, b) => a.customerName.localeCompare(b.customerName));
    }, [customers, quotations, recentVisits, searchText]);

    return (
        <div className="cqPage">
            <div className="cqHead">
                <div>
                    <h1>Customer &amp; Quotation Management</h1>
                    <p>Welcome back! Here&apos;s what&apos;s happening today.</p>
                </div>
            </div>

            <div className="cqCard">
                <h3>Customers &amp; Quotations</h3>
                <div className="cqToolbar">
                    <div className="cqSearchWrap">
                        <input
                            type="text"
                            placeholder="Search by name or contact..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </div>
                </div>

                {error ? <div className="cqError">{error}</div> : null}
                {loading ? (
                    <p className="cqEmpty">Loading...</p>
                ) : rows.length === 0 ? (
                    <p className="cqEmpty">No customer data found.</p>
                ) : (
                    <div className="cqTableWrap">
                        <table className="cqTable">
                            <thead>
                                <tr>
                                    <th>Customer Name</th>
                                    <th>Contact</th>
                                    <th>Priority</th>
                                    <th>Quotation Status</th>
                                    <th>Last Contact</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={`cq-row-${row.id}`}>
                                        <td>{row.customerName}</td>
                                        <td>{row.contact}</td>
                                        <td>
                                            <span className={`cqPill cqPriority ${priorityClass(row.priority)}`}>
                                                Priority {row.priority}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className={`cqPill cqStatus ${quotationStatusClass(row.quotationStatus)}`}
                                            >
                                                {capitalize(row.quotationStatus)}
                                            </span>
                                        </td>
                                        <td>{row.lastContact}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="cqDetailsBtn"
                                                onClick={() => setSelectedItem(row)}
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selectedItem ? (
                <div className="cqModalOverlay" onClick={() => setSelectedItem(null)}>
                    <div className="cqModalCard" onClick={(e) => e.stopPropagation()}>
                        <div className="cqModalHead">
                            <h3>Visit Details</h3>
                            <button
                                type="button"
                                className="cqCloseBtn"
                                onClick={() => setSelectedItem(null)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="cqModalGrid">
                            <div>
                                <p className="cqLabel">Customer</p>
                                <strong>{selectedItem.customerName}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Salesperson</p>
                                <strong>{selectedItem.salesperson}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Sales Person Area</p>
                                <strong>{selectedItem.salespersonArea || "-"}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Visit Date</p>
                                <strong>{formatVisitDateTime(selectedItem.visitDateRaw || selectedItem.lastContact)}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Status</p>
                                <strong>{capitalize(selectedItem.quotationStatus)}</strong>
                            </div>
                            <div className="cqFull">
                                <p className="cqLabel">Location</p>
                                <strong>{selectedItem.location}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Customer Phone</p>
                                <strong>{selectedItem.rawVisit?.customer_phone || selectedItem.rawVisit?.customer_mobile_no || "-"}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Customer Type</p>
                                <strong>{selectedItem.rawVisit?.customer_type || "-"}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Last PO Reference</p>
                                <strong>{selectedItem.rawVisit?.last_po_reference || "-"}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Competitor</p>
                                <strong>{selectedItem.rawVisit?.competitor || "-"}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Product Category</p>
                                <strong>
                                    {selectedItem.rawVisit?.product_category_name ||
                                        selectedItem.rawVisit?.product_category ||
                                        selectedItem.rawVisit?.product_category_id ||
                                        "-"}
                                </strong>
                            </div>
                            <div>
                                <p className="cqLabel">Customer Priority</p>
                                <strong>{selectedItem.rawVisit?.customer_priority || "-"}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Visit Agenda</p>
                                <strong>{selectedItem.rawVisit?.visit_agenda || "-"}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Next Follow-up Date</p>
                                <strong>{formatDateOnly(selectedItem.rawVisit?.next_followup_date)}</strong>
                            </div>
                            <div>
                                <p className="cqLabel">Assigned Salesperson ID</p>
                                <strong>{selectedItem.rawVisit?.assigned_sales_person_id || "-"}</strong>
                            </div>
                        </div>

                        <div className="cqSection">
                            <h4>Visit Notes</h4>
                            <div className="cqNotesBox">{selectedItem.notes}</div>
                        </div>

                        <div className="cqSection">
                            <h4>Action Items</h4>
                            <ul className="cqActionList">
                                {extractVisitActionItems(selectedItem.rawVisit).map((item, idx) => (
                                    <li key={`cq-action-${idx}`}>{item}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="cqSection">
                            <h4>Meeting Persons</h4>
                            <ul className="cqActionList">
                                {extractMeetingPersons(selectedItem.rawVisit).length > 0 ? (
                                    extractMeetingPersons(selectedItem.rawVisit).map((person, idx) => {
                                        const view = formatMeetingPerson(person);
                                        return (
                                            <li key={`cq-meeting-${idx}`}>
                                                <strong>{view.name}</strong>
                                                {view.details.length > 0 ? (
                                                    <div style={{ marginTop: "4px", fontSize: "14px", color: "#4b5563" }}>
                                                        {view.details.join(" | ")}
                                                    </div>
                                                ) : null}
                                            </li>
                                        );
                                    })
                                ) : (
                                    <li>No meeting person added.</li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function priorityClass(value) {
    if (value === "A") return "cqPrA";
    if (value === "B") return "cqPrB";
    return "cqPrC";
}

function quotationStatusClass(value) {
    const v = String(value || "pending").toLowerCase();
    if (v.includes("approve")) return "cqStApproved";
    if (v.includes("sent")) return "cqStSent";
    if (v.includes("reject")) return "cqStRejected";
    return "cqStPending";
}

function formatPriority(raw) {
    const val = String(raw || "C").trim().toUpperCase();
    if (val.includes("A")) return "A";
    if (val.includes("B")) return "B";
    return "C";
}

function formatQuotationStatus(raw) {
    const val = String(raw || "pending").trim().toLowerCase();
    if (val.includes("approve")) return "approved";
    if (val.includes("sent")) return "sent";
    if (val.includes("reject")) return "rejected";
    return "pending";
}

function capitalize(str) {
    const txt = String(str || "");
    if (!txt) return "-";
    return txt.charAt(0).toUpperCase() + txt.slice(1);
}

function formatDateOnly(date) {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date);
    return parsed.toLocaleDateString("en-GB");
}

function formatVisitDateTime(date) {
    if (!date || date === "-") return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date);
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    const hours = String(parsed.getHours()).padStart(2, "0");
    const minutes = String(parsed.getMinutes()).padStart(2, "0");
    const seconds = String(parsed.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
}

function extractVisitActionItems(visit) {
    const candidates = [
        visit?.action_items,
        visit?.action_item,
        visit?.follow_up_action,
        visit?.followup_action,
        visit?.follow_up_plan,
        visit?.next_action_plan,
        visit?.next_action,
    ];

    for (const raw of candidates) {
        if (Array.isArray(raw)) {
            const normalized = raw.map((x) => String(x || "").trim()).filter(Boolean);
            if (normalized.length > 0) return normalized;
        }

        if (typeof raw === "string" && raw.trim()) {
            try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    const normalized = parsed.map((x) => String(x || "").trim()).filter(Boolean);
                    if (normalized.length > 0) return normalized;
                }
            } catch (_e) {
                const normalized = raw
                    .split(/\r?\n|,|;/)
                    .map((x) => x.trim())
                    .filter(Boolean);
                if (normalized.length > 0) return normalized;
            }
        }
    }

    return ["No action item added."];
}

function extractMeetingPersons(visit) {
    const raw = visit?.meeting_persons_details;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") return [raw];
    if (typeof raw === "string" && raw.trim()) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
            if (parsed && typeof parsed === "object") return [parsed];
        } catch (_e) {
            return [];
        }
    }
    return [];
}

function extractSalespersonArea(row) {
    if (!row) return "";
    const rawArea =
        row.sales_person_area ||
        row.area ||
        row.salesPersonArea ||
        row.salesperson_area ||
        "";
    if (typeof rawArea === "string") {
        return rawArea.trim();
    }
    if (rawArea == null) return "";
    return String(rawArea).trim();
}

function formatMeetingPerson(person) {
    if (person == null) return { name: "-", details: [] };
    if (typeof person === "string") return { name: person, details: [] };

    const name = person.meeting_person_name || person.meeting_name || person.person_name || person.name || "-";
    const details = [];
    const usedKeys = new Set(["meeting_person_name", "meeting_name", "person_name", "name"]);
    const knownFields = [
        { label: "Designation", keys: ["meeting_designation", "designation", "meeting_person_designation"] },
        { label: "Department", keys: ["meeting_department", "department"] },
        { label: "Mobile", keys: ["meeting_mobile", "mobile", "phone", "contact_no"] },
        { label: "Email", keys: ["meeting_email", "email", "mail"] },
        { label: "Company", keys: ["meeting_company", "company", "organization"] },
        { label: "Address", keys: ["meeting_address", "address"] },
    ];

    knownFields.forEach((field) => {
        const value = pickFirstNonEmpty(person, field.keys);
        if (value != null) {
            details.push(`${field.label}: ${value}`);
            field.keys.forEach((k) => usedKeys.add(k));
        }
    });

    Object.entries(person).forEach(([key, value]) => {
        if (usedKeys.has(key)) return;
        if (value == null || typeof value === "object") return;
        const text = String(value).trim();
        if (!text) return;
        details.push(`${toTitleLabel(key)}: ${text}`);
    });

    return { name: String(name), details };
}

function pickFirstNonEmpty(obj, keys) {
    for (const key of keys) {
        const value = obj?.[key];
        if (value == null) continue;
        const text = String(value).trim();
        if (text) return text;
    }
    return null;
}

function toTitleLabel(key) {
    return String(key || "")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

function resolveTime(row) {
    const dt = new Date(
        row?.visit_date || row?.sale_created_isodt || row?.sale_date || row?.created_at || ""
    );
    if (Number.isNaN(dt.getTime())) return 0;
    return dt.getTime();
}

function buildCustomerKeys(row) {
    const keys = [
        row?.customer_id,
        row?.cus_id,
        row?.sale_customer_id,
        row?.customer_code,
        row?.customer_name,
        row?.cus_name,
    ]
        .map((v) => String(v || "").trim().toLowerCase())
        .filter(Boolean);
    return Array.from(new Set(keys));
}

function extractArrayRows(payload) {
    if (Array.isArray(payload?.message)) return payload.message;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
}

function extractRecentVisits(payload) {
    const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.message)
            ? payload.message
            : Array.isArray(payload?.result)
              ? payload.result
              : [];
    return rows.filter((row) => row && (row.visit_date || row.customer_name));
}

async function fetchSalesCustomersData(headers) {
    let lastError = null;
    for (const url of SALES_CUSTOMER_APIS) {
        try {
            return await axios.get(url, { headers });
        } catch (err) {
            lastError = err;
        }
    }
    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load customers."
    );
}

async function fetchQuotationsData(headers) {
    let lastError = null;
    for (const url of QUOTATION_APIS) {
        try {
            return await axios.post(url, { reqPayload: {} }, { headers });
        } catch (err) {
            lastError = err;
        }
    }
    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load quotations."
    );
}

async function fetchRecentSalesVisitsData(headers, limit = 300) {
    let lastError = null;
    for (const url of RECENT_SALES_VISIT_APIS) {
        try {
            return await axios.get(url, { headers, params: { include_all: 1, limit } });
        } catch (errGet) {
            lastError = errGet;
        }
        try {
            return await axios.post(url, { include_all: 1, limit }, { headers });
        } catch (errPost) {
            lastError = errPost;
        }
    }
    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load recent sales visits."
    );
}

const mapStateToProps = (state) => ({
    authInfo: state.authInfoReducer,
});

export default connect(mapStateToProps)(Customers);
